import React, { useState, useMemo, useEffect } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Circle } from 'react-leaflet';
import { Box, Typography, Paper, TextField, IconButton } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { ftzCoordinates } from '@/constants/ftz-coordinates';
import { usPorts } from '@/constants/port-coordinates';
import { MAP_ICONS, MAP_CONFIG } from '@/constants/map-icons';
import { CardHeading } from "@/pages/COMMON/dashboard/potential/tabs/preferential-origin/components";
import axios from 'axios';
import { useAppSelector } from "@/store";
import CircularProgress from '@mui/material/CircularProgress';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix Leaflet default icon issue
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

interface FTZMapProps {
  selectedPorts: string[];
}

const FTZMap: React.FC<FTZMapProps> = ({ selectedPorts }) => {
  const { selectedClient } = useAppSelector((state) => state.client);
  const [zipcode, setZipcode] = useState("");
  const [warehouseLocation, setWarehouseLocation] = useState<{
    coordinates: [number, number];
    name: string;
  } | null>(null);
  const [nearbyFTZs, setNearbyFTZs] = useState<string[]>([]);
  const [radius, setRadius] = useState(MAP_CONFIG.RADIUS_IN_MILES);
  const [isGeocoding, setIsGeocoding] = useState(false);

  useEffect(() => {
    setZipcode("");
    setWarehouseLocation(null);
    setNearbyFTZs([]);
    setRadius(MAP_CONFIG.RADIUS_IN_MILES);
  }, [selectedClient]);

  const calculateDistance = (coord1: [number, number], coord2: [number, number]): number => {
    const [lat1, lon1] = coord1;
    const [lat2, lon2] = coord2;
    const R = 3959; // Earth's radius in miles
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLon = (lon2 - lon1) * Math.PI / 180;
    const a = 
      Math.sin(dLat/2) * Math.sin(dLat/2) +
      Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
      Math.sin(dLon/2) * Math.sin(dLon/2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
    return Math.round(R * c); // Round to nearest mile
  };

  const handleZipcodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only allow numbers and limit to 5 digits
    const value = e.target.value.replace(/\D/g, '').slice(0, 5);
    setZipcode(value);
  };

  const handleZipcodeSearch = async () => {
    // Validate zipcode format
    if (!/^\d{5}$/.test(zipcode)) {
      console.error('Please enter a valid 5-digit zipcode');
      return;
    }

    setIsGeocoding(true);

    try {
      // Using Nominatim (OpenStreetMap) - free geocoding service with no API key required
      const response = await axios.get(
        `https://nominatim.openstreetmap.org/search?q=${zipcode}&format=json&limit=1&countrycodes=us`,
        {
          headers: {
            'User-Agent': 'Riffs-Frontend/1.0' // Required by Nominatim usage policy
          }
        }
      );

      if (response.data.length > 0) {
        const { lat, lon } = response.data[0];
        const coordinates: [number, number] = [parseFloat(lat), parseFloat(lon)]; // Note: Leaflet uses [lat, lng] format
        const locationName = response.data[0].display_name.split(',')[0];

        setWarehouseLocation({
          coordinates,
          name: locationName
        });

        const nearby = Object.entries(ftzCoordinates)
          .filter(([_, ftz]) => calculateDistance(coordinates, [ftz.coordinates[1], ftz.coordinates[0]]) <= radius)
          .map(([ftzNumber]) => ftzNumber);

        setNearbyFTZs(nearby);
      } else {
        console.error('Invalid zipcode: No location found');
      }
    } catch (error) {
      console.error('Error geocoding zipcode:', error);
    } finally {
      setIsGeocoding(false);
    }
  };

  const handleRadiusChange = (newRadius: number) => {
    setRadius(newRadius);
    if (warehouseLocation) {
      const nearby = Object.entries(ftzCoordinates)
        .filter(([_, ftz]) => calculateDistance(warehouseLocation.coordinates, [ftz.coordinates[1], ftz.coordinates[0]]) <= newRadius)
        .map(([ftzNumber]) => ftzNumber);
      setNearbyFTZs(nearby);
    }
  };

  const portCoordinates = useMemo(() => {
    return usPorts.reduce((acc: { [key: string]: { name: string; coordinates: [number, number] } }, port) => {
      acc[port.portCode] = {
        name: port.city,
        coordinates: [port.coordinates.lat, port.coordinates.lng] as [number, number] // Leaflet format
      };
      return acc;
    }, {});
  }, []);

  const closestFTZs = useMemo(() => {
    const ftzSet = new Set<string>();
    const allClosestFTZs: Array<{
      ftzNumber: string;
      name: string;
      coordinates: [number, number];
      portCode?: string;
      source: 'port' | 'warehouse';
      distance: number;
    }> = [];

    // Get closest FTZs for each port
    selectedPorts.forEach(port => {
      const portInfo = portCoordinates[port];
      if (!portInfo) return;

      const ftzDistances = Object.entries(ftzCoordinates)
        .map(([ftzNumber, ftz]) => ({
          ftzNumber,
          name: ftz.name,
          coordinates: [ftz.coordinates[1], ftz.coordinates[0]] as [number, number], // Convert to Leaflet format
          portCode: port,
          source: 'port' as const,
          distance: calculateDistance(portInfo.coordinates, [ftz.coordinates[1], ftz.coordinates[0]])
        }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 5);

      ftzDistances.forEach(ftz => {
        if (!ftzSet.has(ftz.ftzNumber)) {
          ftzSet.add(ftz.ftzNumber);
          allClosestFTZs.push(ftz);
        }
      });
    });

    // Add closest FTZs for warehouse location if exists
    if (warehouseLocation) {
      const warehouseFTZs = Object.entries(ftzCoordinates)
        .map(([ftzNumber, ftz]) => ({
          ftzNumber,
          name: ftz.name,
          coordinates: [ftz.coordinates[1], ftz.coordinates[0]] as [number, number],
          source: 'warehouse' as const,
          distance: calculateDistance(warehouseLocation.coordinates, [ftz.coordinates[1], ftz.coordinates[0]])
        }))
        .sort((a, b) => a.distance - b.distance)
        .slice(0, 5);

      warehouseFTZs.forEach(ftz => {
        if (!ftzSet.has(ftz.ftzNumber)) {
          ftzSet.add(ftz.ftzNumber);
          allClosestFTZs.push(ftz);
        }
      });
    }

    return allClosestFTZs;
  }, [selectedPorts, portCoordinates, warehouseLocation]);

  // Convert radius from miles to meters for Leaflet circle
  const radiusInMeters = radius * 1609.34;

  return (
    <Paper sx={{ p: 3, height: '500px', boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <CardHeading text="Selected Ports and FTZ Zones" />
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <TextField
            size="small"
            label="Warehouse Zipcode"
            value={zipcode}
            onChange={handleZipcodeChange}
            sx={{ width: 150 }}
            error={zipcode.length > 0 && !/^\d{5}$/.test(zipcode)}
            helperText={zipcode.length > 0 && !/^\d{5}$/.test(zipcode) ? "Enter 5 digits" : ""}
            inputProps={{
              inputMode: 'numeric',
              pattern: '[0-9]*'
            }}
            onKeyPress={(e) => {
              if (e.key === 'Enter' && /^\d{5}$/.test(zipcode)) {
                handleZipcodeSearch();
              }
            }}
            disabled={isGeocoding}
          />
          <TextField
            size="small"
            type="number"
            label="Radius (miles)"
            value={radius}
            onChange={(e) => handleRadiusChange(Number(e.target.value))}
            sx={{ width: 120 }}
            InputProps={{
              inputProps: { min: 1, max: 500 }
            }}
            disabled={isGeocoding}
          />
          <IconButton 
            onClick={handleZipcodeSearch} 
            color="primary"
            disabled={isGeocoding}
          >
            {isGeocoding ? <CircularProgress size={24} /> : <SearchIcon />}
          </IconButton>
        </Box>
      </Box>
      
      <Box sx={{ position: 'relative', width: '100%', height: 'calc(100% - 80px)', overflow: 'hidden' }}>
        <MapContainer
          center={warehouseLocation ? warehouseLocation.coordinates : MAP_CONFIG.DEFAULT_CENTER}
          zoom={warehouseLocation ? MAP_CONFIG.WAREHOUSE_ZOOM : MAP_CONFIG.DEFAULT_ZOOM}
          style={{ height: '100%', width: '100%' }}
          key={warehouseLocation ? `${warehouseLocation.coordinates[0]}-${warehouseLocation.coordinates[1]}` : 'default'}
          >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/attributions">CARTO</a>'
            url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
            maxZoom={19}
          />
          
          {/* Warehouse location and radius */}
            {warehouseLocation && (
              <>
              <Circle
                center={warehouseLocation.coordinates}
                radius={radiusInMeters}
                pathOptions={{
                  fillColor: '#0B494B',
                  fillOpacity: 0.2,
                  color: '#0B494B',
                  weight: 2
                }}
              />
              <Marker position={warehouseLocation.coordinates} icon={MAP_ICONS.warehouse}>
                <Popup>
                  <Box component="div">
                    <Typography variant="body2" fontWeight="bold">Warehouse Location</Typography>
                    <Typography variant="body2">{warehouseLocation.name}</Typography>
                  </Box>
                </Popup>
                </Marker>
              </>
            )}

          {/* Port markers */}
          {selectedPorts.map(portCode => {
            const port = portCoordinates[portCode];
            if (!port) return null;
            
            return (
              <Marker key={portCode} position={port.coordinates} icon={MAP_ICONS.port}>
                <Popup>
                  <Box component="div">
                    <Typography variant="body2" fontWeight="bold">Port: {port.name}</Typography>
                    <Typography variant="body2">Code: {portCode}</Typography>
                  </Box>
                </Popup>
              </Marker>
            );
          })}
            
          {/* FTZ markers */}
            {closestFTZs.map(({ ftzNumber, name, coordinates, portCode, source }) => {
              const distanceFromWarehouse = warehouseLocation 
                ? calculateDistance(coordinates, warehouseLocation.coordinates)
                : Infinity;
              
              const isWithinRadius = distanceFromWarehouse <= radius;
            const icon = isWithinRadius ? MAP_ICONS.nearbyFtz : MAP_ICONS.ftz;

              return (
                <Marker
                  key={`ftz-${ftzNumber}-${source}-${portCode ?? 'warehouse'}`} 
                position={coordinates}
                icon={icon}
              >
                <Popup>
                  <Box component="div">
                    <Typography variant="body2" fontWeight="bold">FTZ {ftzNumber}</Typography>
                    <Typography variant="body2">{name}</Typography>
                    {warehouseLocation && (
                      <Typography variant="body2">Distance: {distanceFromWarehouse} miles</Typography>
                    )}
                    <Typography variant="body2">Source: {source === 'port' ? `Port ${portCode}` : 'Warehouse'}</Typography>
                  </Box>
                </Popup>
                </Marker>
              );
            })}
        </MapContainer>
        
        <Box sx={{ position: 'absolute', bottom: 10, left: 10, backgroundColor: 'rgba(255,255,255,0.9)', padding: 1, borderRadius: 1, boxShadow: 1 }}>
          <Typography variant="body2">
            <Box component="span" sx={{ color: '#2563eb', marginRight: 0.5 }}>●</Box> 
            Ports
          </Typography>
          <Typography variant="body2">
            <Box component="span" sx={{ color: '#800080', marginRight: 0.5 }}>●</Box> 
            FTZ Zones
          </Typography>
          {nearbyFTZs.length > 0 && (
            <Typography variant="body2">
              <Box component="span" sx={{ color: '#00FF00', marginRight: 0.5 }}>●</Box> 
              FTZs Within {radius} Miles
            </Typography>
          )}
          {warehouseLocation && (
            <Typography variant="body2">
              <Box component="span" sx={{ color: '#0B494B', marginRight: 0.5 }}>●</Box> 
              Warehouse Location
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default FTZMap; 